import * as React from 'react';
import glamorous from 'glamorous';
import ErrorOutline from '@material-ui/icons/ErrorOutline';
import ContactSupportOutlinedIcon from '@material-ui/icons/ContactSupportOutlined';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Button } from '@material-ui/core';
import { SupportEmailDialog } from '../home/Support/SupportEmailPage';
import { appInsights } from './ApplicationInsights';
import { UserFeature } from '../types/UserFeature';

interface Props {
    children: React.ReactNode;
    componentName?: string;
}

interface State {
    hasError: boolean;
    error: Error | null;
    showSupportDialog: boolean;
}

interface NetworkEntry {
    url: string;
    method: string;
    status: number;
    duration: number;
    bodyError?: string;
}

// tslint:disable-next-line: no-any
const ErrorWrapper = glamorous.div<{ theme?: any }>(() => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexFlow: 'column wrap',
    height: '100%',
    padding: '20px 1rem',
    marginTop: '50px',
    textAlign: 'center',

    [`& .error-icon-wrapper`]: {
        marginBottom: '40px',
        color: 'red',
        [`& .label`]: {
            fontWeight: 700,
            fontSize: '24px',
            marginTop: '10px',
        },
    },

    [`& .error-icon-container`]: {
        border: '5px solid',
        borderRadius: '50%',
        width: '100px',
        height: '100px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: 'inherit',
        margin: '0 auto 10px auto',
    },

    [`& .error-message-wrapper`]: {
        textAlign: 'center',
        [`& h4`]: {
            marginBottom: '8px',
        },
        [`& p`]: {
            color: '#666',
        },
    },

    [`& .button-group`]: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        justifyContent: 'center',
    },
}));

const ERROR_PATTERNS = ['object reference', 'nullreferenceexception', 'exception', 'unhandled', 'error'];

export class AppInsightsErrorBoundary extends React.Component<Props, State> {
    private consoleLogs: string[] = [];
    private networkLogs: NetworkEntry[] = [];
    // tslint:disable-next-line: no-any
    private originalConsoleError: (...args: any[]) => void;
    private originalFetch: typeof window.fetch;

    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null, showSupportDialog: false };
        this.onClickSupport = this.onClickSupport.bind(this);
        this.onCloseSupportDialog = this.onCloseSupportDialog.bind(this);

        // Intercept console errors
        this.originalConsoleError = console.error;
        // tslint:disable-next-line: no-any
        console.error = (...args: any[]) => {
            // tslint:disable-next-line: no-any
            this.consoleLogs.push(`[ERROR] ${args.map((a: any) => String(a)).join(' ')}`);
            if (this.consoleLogs.length > 50) { this.consoleLogs.shift(); }
            this.originalConsoleError.apply(console, args);
        };

        // Intercept fetch to capture status codes and body errors
        this.originalFetch = window.fetch.bind(window);
        window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
            const url = typeof input === 'string'
                ? input
                : input instanceof URL
                    ? input.toString()
                    : (input as Request).url;
            const method = (init && init.method ? init.method : 'GET').toUpperCase();
            const start = performance.now();

            try {
                const response = await this.originalFetch(input, init);
                const duration = performance.now() - start;
                const entry: NetworkEntry = { url: url.split('?')[0], method, status: response.status, duration };

                if (!response.ok) {
                    try {
                        const text = await response.clone().text();
                        const lower = text.toLowerCase();
                        if (ERROR_PATTERNS.some(p => lower.includes(p))) {
                            entry.bodyError = text.substring(0, 300);
                        }
                    } catch { /* ignore body read errors */ }
                }

                this.networkLogs.push(entry);
                if (this.networkLogs.length > 50) { this.networkLogs.shift(); }
                return response;
            } catch (e) {
                const duration = performance.now() - start;
                this.networkLogs.push({ url: url.split('?')[0], method, status: 0, duration, bodyError: String(e) });
                if (this.networkLogs.length > 50) { this.networkLogs.shift(); }
                throw e;
            }
        };
    }

    componentWillUnmount() {
        console.error = this.originalConsoleError;
        window.fetch = this.originalFetch;
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        appInsights.trackException({
            exception: error,
            properties: {
                source: 'ErrorBoundary',
                componentName: this.props.componentName || 'Unknown',
                componentStack: errorInfo.componentStack,
                tenantId: UserFeature.Tenant,
                userId: UserFeature.UserId
            }
        });
    }

    private generateDiagnosticInfo(): string {
        const { error } = this.state;

        const networkOutput = this.networkLogs.length > 0
            ? this.networkLogs.map(e => {
                let line = `  [${e.method}] ${e.url} — ${e.status} (${e.duration.toFixed(0)}ms)`;
                if (e.bodyError) { line += `\n    Body: ${e.bodyError}`; }
                return line;
            }).join('\n')
            : 'No network requests captured.';

        const consoleOutput = this.consoleLogs.length > 0
            ? this.consoleLogs.join('\n')
            : 'No console errors captured.';

        return `=== Error Details ===
Message: ${error ? error.message : 'Unknown error'}

=== Stack Trace ===
${error ? error.stack : 'Not available'}

=== Environment ===
Component: ${this.props.componentName || 'Unknown'}
URL: ${window.location.href}
Browser: ${navigator.userAgent}
Time: ${new Date().toISOString()}

=== Console Errors ===
${consoleOutput}

=== Network Requests ===
${networkOutput}`;
    }

    private onClickSupport() {
        this.setState({ showSupportDialog: true });
    }

    private onCloseSupportDialog() {
        this.setState({ showSupportDialog: false });
    }

    render() {
        if (this.state.hasError) {
            return (
                <>
                    <ErrorWrapper>
                        <div className="error-icon-wrapper">
                            <div className="error-icon-container">
                                <ErrorOutline style={{ fontSize: 50 }} />
                            </div>
                            <div className="label">Something went wrong</div>
                        </div>
                        <div className="error-message-wrapper">
                            <h4>An unexpected error has occurred.</h4>
                            <p>The error has been logged. Please try again or refresh the page.</p>
                        </div>
                        <br />
                        <div className="button-group">
                            <Button
                                style={{ color: 'rgb(81, 115, 255)' }}
                                onClick={() => this.setState({ hasError: false, error: null })}
                                startIcon={<RefreshIcon fontSize="large" />}
                            >
                                Try Again
                            </Button>
                            <Button
                                style={{ color: 'rgb(81, 115, 255)', borderColor: 'rgb(81, 115, 255)' }}
                                onClick={this.onClickSupport}
                                startIcon={<ContactSupportOutlinedIcon fontSize="large" />}
                            >
                                Get Support
                            </Button>
                        </div>
                    </ErrorWrapper>
                    {this.state.showSupportDialog && (
                        <SupportEmailDialog
                            isAuthenticated={true}
                            onClose={this.onCloseSupportDialog}
                            initialNote={this.generateDiagnosticInfo()}
                        />
                    )}
                </>
            );
        }

        return this.props.children;
    }
}
